<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    type CustomerUser as User,
    type CustomerWordDocumentTemplate,
    WordDocumentTemplatesService,
    WordTemplateStatus
  } from '@/types/generated'
  import { TableCell, TableRow } from '@/components/ui/table'
  import type { FetchDataOptions, PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { genericErrorToast } from '@/lib/toasts'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { Plus, Upload } from 'lucide-vue-next'
  import NeutralBanner from '@/components/ui/informational-banner/NeutralBanner.vue'

  const router = useRouter()
  const showDeleteModal = ref(false)
  const templateToDelete = ref<CustomerWordDocumentTemplate | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)
  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user as User)
  const { translateLawTopic } = useLawTopicTranslation()
  const { translateLawField } = useLawFieldTranslation()

  // Helper function to add fallback dash for display
  const displayLawField = (lawField: string | null | undefined): string => {
    return translateLawField(lawField) || '-'
  }

  // Add translations
  const { t } = useTranslation({
    en: {
      createTemplate: 'Create Template',
      bulkUpload: 'Bulk Upload',
      search: 'Search',
      lawField: 'Law Field',
      allLawFields: 'All Law Fields',
      name: 'Name',
      type: 'Type',
      status: 'Status',
      lawTopic: 'Law Topic',
      updated: 'Updated',
      firm: 'Firm',
      edit: 'Edit',
      delete: 'Delete',
      inReview: 'In Review',
      approved: 'Approved',
      draft: 'Draft',
      noTemplatesFound: 'No templates found.',
      deleteNotPossible: 'Delete Not Possible',
      cannotDeleteJupusTemplate: 'Jupus templates cannot be deleted.',
      close: 'Close',
      deleteTemplate: 'Delete Template',
      deleteTemplateConfirmation:
        'Are you sure you want to delete this template? This action cannot be undone.',
      cancel: 'Cancel',
      confirmDelete: 'Delete',
      bannerText:
        'Documents with complex calculations, deeply nested structures, or very intricate statements may not produce optimal results.'
    },
    de: {
      createTemplate: 'Vorlage erstellen',
      bulkUpload: 'Massenupload',
      search: 'Suchen',
      lawField: 'Rechtsgebiet',
      allLawFields: 'Alle Rechtsgebiete',
      name: 'Name',
      type: 'Typ',
      status: 'Status',
      lawTopic: 'Rechtsthema',
      updated: 'Aktualisiert',
      firm: 'Kanzlei',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      inReview: 'In Prüfung',
      approved: 'Freigegeben',
      draft: 'Entwurf',
      noTemplatesFound: 'Keine Vorlagen gefunden.',
      deleteNotPossible: 'Löschen nicht möglich',
      cannotDeleteJupusTemplate: 'Jupus-Vorlagen können nicht gelöscht werden.',
      close: 'Schließen',
      deleteTemplate: 'Vorlage löschen',
      deleteTemplateConfirmation:
        'Sind Sie sicher, dass Sie diese Vorlage löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirmDelete: 'Löschen',
      bannerText:
        'Dokumente mit komplexen Berechnungen, tief verschachtelten "wenn/dann" Strukturen oder sehr komplizierten Anweisungen liefern möglicherweise keine optimalen Ergebnisse.'
    }
  })

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }
  })

  const fetchWordTemplates = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<any>> => {
    const response = await WordDocumentTemplatesService.wordDocumentTemplatesList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    })

    return {
      results: response.results || [],
      next: response.next || null,
      previous: response.previous || null,
      count: response.count || 0
    }
  }

  const openDeleteModal = (template: CustomerWordDocumentTemplate) => {
    templateToDelete.value = template
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const confirmDelete = async () => {
    if (templateToDelete.value) {
      try {
        if (!templateToDelete.value.uuid) return
        await WordDocumentTemplatesService.wordDocumentTemplatesDestroy({
          uuid: templateToDelete.value.uuid
        })
        closeDeleteModal()
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const createTemplate = async () => {
    try {
      const newTemplate = await WordDocumentTemplatesService.wordDocumentTemplatesCreate({
        formData: {
          name: 'Neue Vorlage',
          law_field: '',
          law_topic: '',
          description: '(leer)',
          system_prompt: '(leer)',
          is_jupus_template: false,
          replaceable_variables: null
        }
      })
      await router.push(
        ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
          ':uuid',
          newTemplate.uuid ? newTemplate.uuid : ''
        )
      )
    } catch (error) {
      console.error('Failed to create new template:', error)
      genericErrorToast()
    }
  }
</script>

<template>
  <SettingsPage v-if="user">
    <template #actions>
      <div class="flex gap-2">
        <button
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
          @click="router.push(ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATES_BULK)"
        >
          <Upload class="h-4 w-4" />
          {{ t('bulkUpload') }}
        </button>
        <button
          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
            text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
            focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
          @click="createTemplate"
        >
          <Plus class="h-4 w-4" />
          {{ t('createTemplate') }}
        </button>
      </div>
    </template>
    <div class="space-y-4">
      <PaginatedTableView
        ref="paginatedTableRef"
        :fetchData="fetchWordTemplates"
        searchableFields="template name, law field, law topic"
      >
        <template #before-table>
          <NeutralBanner class="-mt-4 mb-4">
            {{ t('bannerText') }}
          </NeutralBanner>
        </template>
        <template #table-head>
          <TableCell class="w-1/4 py-2 font-medium">{{ t('name') }}</TableCell>
          <TableCell class="w-1/8 py-2 font-medium">{{ t('type') }}</TableCell>
          <TableCell class="w-1/8 py-2 font-medium">{{ t('status') }}</TableCell>
          <TableCell class="w-1/8 py-2 font-medium">{{ t('lawField') }}</TableCell>
          <TableCell class="w-1/8 py-2 font-medium">{{ t('lawTopic') }}</TableCell>
          <TableCell class="w-1/8 py-2 font-medium">{{ t('updated') }}</TableCell>
          <TableCell class="w-12 py-2"></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="template in items"
            :key="template.uuid"
            class="cursor-pointer hover:bg-gray-50"
            @click="
              router.push(
                ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
                  ':uuid',
                  template.uuid ? template.uuid : ''
                )
              )
            "
          >
            <TableCell class="py-4">{{ template.name }}</TableCell>
            <TableCell class="py-1">
              <span
                v-if="template.is_jupus_template"
                class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700
                  ring-1 ring-inset ring-purple-700/10"
              >
                Jupus
              </span>
              <span
                v-else
                class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1
                  ring-inset ring-red-700/10"
              >
                {{ t('firm') }}
              </span>
            </TableCell>
            <TableCell class="py-1">
              <span
                v-if="template.status === WordTemplateStatus.IN_REVIEW"
                class="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700
                  ring-1 ring-inset ring-yellow-700/10"
              >
                {{ t('inReview') }}
              </span>
              <span
                v-else-if="template.status === WordTemplateStatus.APPROVED"
                class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1
                  ring-inset ring-green-700/10"
              >
                {{ t('approved') }}
              </span>
              <span
                v-else
                class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1
                  ring-inset ring-gray-700/10"
              >
                {{ t('draft') }}
              </span>
            </TableCell>
            <TableCell class="py-1">{{ displayLawField(template.law_field) }}</TableCell>
            <TableCell class="py-1">{{ translateLawTopic(template.law_topic) || '-' }}</TableCell>
            <TableCell class="py-1">
              {{ new Date(template.updated_at).toLocaleDateString('de-DE') }}
            </TableCell>
            <TableCell
              class="py-1"
              @click.stop
            >
              <TableOptionsButton
                :options="[
                  {
                    label: t('edit'),
                    to: ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATE_DETAIL.replace(
                      ':uuid',
                      template.uuid ? template.uuid : ''
                    )
                  },
                  {
                    label: t('delete'),
                    onClick: () => openDeleteModal(template)
                  }
                ]"
              />
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>
  </SettingsPage>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent v-if="templateToDelete">
      <DialogHeader>
        <template v-if="templateToDelete?.is_jupus_template">
          <DialogTitle>{{ t('deleteNotPossible') }}</DialogTitle>
          <DialogDescription>{{ t('cannotDeleteJupusTemplate') }}</DialogDescription>
        </template>
        <template v-else>
          <DialogTitle>{{ t('deleteTemplate') }}</DialogTitle>
          <DialogDescription>
            {{ t('deleteTemplateConfirmation') }}
          </DialogDescription>
        </template>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="secondary"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          v-if="!templateToDelete?.is_jupus_template"
          variant="destructive"
          @click="confirmDelete"
        >
          {{ t('confirmDelete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
